// The change queue's replay plan (BL-2345, ADR 066 — Offline Mode § 4): which entry runs next and how
// a relist's or a note's client id becomes its server id. Pure, so the order is testable without a
// store or a network. A lot's field edits are not planned here: they are its desired state (BL-2681).
import { readQueuedEntry, type ChangeQueueCall, type ChangeQueueJson } from './change-queue-entry';

/** What the planner reads of a stored entry. */
export interface QueueEntryView {
  id: string;
  seq: number;
  op: string;
  status: 'pending' | 'syncing' | 'failed' | 'refused';
  dependsOn: string | null;
  groupKey: string | null;
  payload: unknown;
}

const NO_GROUPS: ReadonlySet<string> = new Set();

function bySeq<T extends QueueEntryView>(entries: readonly T[]): T[] {
  return [...entries].sort((a, b) => a.seq - b.seq);
}

/**
 * The next entry to replay: the oldest pending one not already tried this pass whose parent has
 * synced. A parent that is still queued, in any status, holds it back, a photo order also waits
 * on its lot's earlier photo adds and removes (BL-2418), and a photo or document removal waits on
 * the queued add of that same item (BUG-2068); nothing else does, so one failure never stops
 * independent entries (AC 3).
 *
 * @param entries - the queue.
 * @param tried - ids this pass already sent and left pending (a server error), not retried again.
 * @param blockedGroups - group keys (`lot:<id>`) whose lot still has an unsettled desired state (BL-2681); their entries wait.
 * @returns the entry, or null when nothing can run.
 */
export function nextRunnable<T extends QueueEntryView>(
  entries: readonly T[],
  tried: ReadonlySet<string>,
  blockedGroups: ReadonlySet<string> = NO_GROUPS,
): T | null {
  const queued = new Set(entries.map((e) => e.id));
  return (
    bySeq(entries).find(
      (e) =>
        e.status === 'pending' &&
        !tried.has(e.id) &&
        // BL-2681: a lot's entries wait while its desired state is unsettled, so its edit lands first.
        (e.groupKey == null || !blockedGroups.has(e.groupKey)) &&
        (e.dependsOn == null || !queued.has(e.dependsOn)) &&
        !waitsOnItemAdd(entries, e),
    ) ?? null
  );
}

/** Each removal op, the add op of the same kind of item, and the path param naming the item. */
const REMOVE_WAITS_ON: Readonly<Record<string, { add: string; param: string }>> = {
  'lot.document.remove': { add: 'lot.document.add', param: 'documentId' },
};

/**
 * BUG-2068: a photo or document removal waits while an earlier add of that same item on the same
 * lot is still queued, in any status. Run first, the removal finds nothing on the server, lands as
 * a no-op and leaves the queue, and the add that lands later brings the item back. A removal of an
 * item the queue never added (it was on the server before going offline) waits on nothing.
 */
function waitsOnItemAdd<T extends QueueEntryView>(entries: readonly T[], entry: T): boolean {
  const rule = REMOVE_WAITS_ON[entry.op];
  if (rule == null || entry.groupKey == null) return false;
  const itemId = callOf(entry)?.params[rule.param];
  if (itemId == null) return false;
  return entries.some(
    (e) =>
      e.seq < entry.seq &&
      e.op === rule.add &&
      e.groupKey === entry.groupKey &&
      asObject(callOf(e)?.body ?? null).id === itemId,
  );
}

function callOf(entry: QueueEntryView): ChangeQueueCall | null {
  const read = readQueuedEntry(entry);
  return read.ok ? read.payload.call : null;
}

function asObject(value: ChangeQueueJson): Record<string, ChangeQueueJson> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {};
}

function replaceDeep(value: ChangeQueueJson, from: string, to: string): ChangeQueueJson {
  if (value === from) return to;
  if (Array.isArray(value)) return value.map((v) => replaceDeep(v, from, to));
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, replaceDeep(v, from, to)]));
  }
  return value;
}

/**
 * Point a call at a new record's server id: every value equal to its client id (a UUID, so it
 * cannot collide with anything else in the call) becomes the server id.
 *
 * @param call - the call to rewrite.
 * @param from - the client id.
 * @param to - the server id.
 * @returns the rewritten call.
 */
export function substituteId(call: ChangeQueueCall, from: string, to: string): ChangeQueueCall {
  const swap = (map: Record<string, string>) =>
    Object.fromEntries(Object.entries(map).map(([k, v]) => [k, v === from ? to : v]));
  return {
    params: swap(call.params),
    query: call.query == null ? null : swap(call.query),
    body: replaceDeep(call.body, from, to),
  };
}

/**
 * Every entry that waits on `id`, directly or through another waiting entry: what can never run
 * once `id` is discarded.
 *
 * @param entries - the queue.
 * @param id - the entry being discarded.
 * @returns its dependents, in queue order.
 */
export function dependentsOf<T extends QueueEntryView>(entries: readonly T[], id: string): T[] {
  const gone = new Set([id]);
  const out: T[] = [];
  for (const e of bySeq(entries)) {
    if (e.dependsOn != null && gone.has(e.dependsOn)) {
      gone.add(e.id);
      out.push(e);
    }
  }
  return out;
}
